/**
 * Extract meaningful error message from API response or error object
 * @param {Error | AxiosError} error - The error object
 * @param {string} fallback - Fallback message if no error message found
 * @returns {string} Error message to display to user
 */
export const getErrorMessage = (error, fallback = "An error occurred. Please try again.") => {
  // Handle axios error responses
  if (error?.response) {
    const { data } = error.response;
    
    // Try to get message from response
    if (data?.message) {
      return data.message;
    }
    
    // Try to get from errors object (validation errors)
    if (data?.errors && typeof data.errors === 'object') {
      const errorMessages = Object.values(data.errors).flat();
      if (errorMessages.length > 0) {
        return Array.isArray(errorMessages[0]) 
          ? errorMessages[0][0] 
          : errorMessages[0];
      }
    }
    
    // Try to get from error field
    if (data?.error) {
      return data.error;
    }
    
    // Fallback to status text
    if (error.response.status === 401) {
      return "Unauthorized. Please login again.";
    }
    if (error.response.status === 403) {
      return "You don't have permission to perform this action.";
    }
    if (error.response.status === 404) {
      return "Resource not found.";
    }
    if (error.response.status === 429) {
      return "Too many requests. Please wait a moment and try again.";
    }
    if (error.response.status === 500) {
      return "Server error. Please try again later.";
    }
  }
  
  // Handle network errors
  if (error?.message === 'Network Error') {
    return "Network error. Please check your connection.";
  }
  
  // Handle timeout
  if (error?.code === 'ECONNABORTED') {
    return "Request timeout. Please try again.";
  }
  
  // Handle generic error message
  if (error?.message) {
    return error.message;
  }
  
  // Fallback message
  return fallback;
};

/**
 * Extract validation errors from API response
 * @param {object} responseData - The error response data
 * @returns {object} Object with field names as keys and error messages as values
 */
export const getValidationErrors = (responseData) => {
  if (!responseData) return {};
  
  // If errors is already in the format we want
  if (responseData.errors && typeof responseData.errors === 'object') {
    const formatted = {};
    Object.entries(responseData.errors).forEach(([key, value]) => {
      formatted[key] = Array.isArray(value) ? value[0] : value;
    });
    return formatted;
  }
  
  return {};
};

export default getErrorMessage;
